import { createAction, props, union, createReducer, on, createSelector } from '@ngrx/store';
import { Injectable, ɵɵdefineInjectable, ɵɵinject } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Subject, throwError, Observable, from, fromEvent, merge, of, EMPTY, timer } from 'rxjs';
import { share, map, startWith, filter, mergeMap, switchMap, exhaustMap, catchError, groupBy, takeUntil } from 'rxjs/operators';
import { HubConnectionState, HubConnectionBuilder, Subject as Subject$1 } from '@microsoft/signalr';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Action to dispatch in order to create a new SignalR hub.
 * @type {?}
 */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@ngrx/effects';
const createSignalRHub = createAction("@ngrx/signalr/createHub", props());
/** @type {?} */
const SIGNALR_HUB_UNSTARTED = "@ngrx/signalr/hubUnstarted";
/**
 * Action dispatched when a hub is at `unstarted` state.
 * @type {?}
 */
const signalrHubUnstarted = createAction(SIGNALR_HUB_UNSTARTED, props());
/**
 * Action to dispatch in order to start a SignalR hub.
 * @type {?}
 */
const startSignalRHub = createAction("@ngrx/signalr/startHub", props());
/**
 * Action to dispatch in order to stop a SignalR hub.
 * @type {?}
 */
const stopSignalRHub = createAction("@ngrx/signalr/stopHub", props());
/**
 * Action to dispatch in order to reconnect to a SignalR hub.
 * It can be automatically dispatched using `createReconnectEffect` effect.
 * @type {?}
 */
const reconnectSignalRHub = createAction("@ngrx/signalr/reconnectHub", props());
/** @type {?} */
const SIGNALR_HUB_FAILED_TO_START = "@ngrx/signalr/hubFailedToStart";
/**
 * Action dispatched when a SignalR failed to start.
 * @type {?}
 */
const signalrHubFailedToStart = createAction(SIGNALR_HUB_FAILED_TO_START, props());
/** @type {?} */
const SIGNALR_CONNECTED = "@ngrx/signalr/connected";
/**
 * Action dispatched when a hub is at `connected` state.
 * @type {?}
 */
const signalrConnected = createAction(SIGNALR_CONNECTED, props());
/** @type {?} */
const SIGNALR_DISCONNECTED = "@ngrx/signalr/disconnected";
/**
 * Action dispatched when a hub is at `disconnected` state.
 * @type {?}
 */
const signalrDisconnected = createAction(SIGNALR_DISCONNECTED, props());
/** @type {?} */
const SIGNALR_ERROR = "@ngrx/signalr/error";
/**
 * Action dispatched when an error occured with a SignalR hub.
 * @type {?}
 */
const signalrError = createAction(SIGNALR_ERROR, props());
/**
 * Action dispatched when a SignalR cannot be found, when doing any action.
 * @type {?}
 */
const hubNotFound = createAction("@ngrx/signalr/hubNotFound", props());
/** @type {?} */
const signalRAction = union({
    createSignalRHub,
    signalrHubUnstarted,
    startSignalRHub,
    stopSignalRHub,
    reconnectSignalRHub,
    signalrHubFailedToStart,
    signalrConnected,
    signalrDisconnected,
    signalrError,
    hubNotFound,
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
let testingEnabled = false;
/** @type {?} */
let hubCreationFunc;
/**
 * Enable testing of the ngrx-signalr-core package.
 * Only use this function for testing purpose ONLY.
 * \@param func A function that will used to create a new SignalR hub.
 * @type {?}
 */
const enableTesting = (/**
 * @param {?} func
 * @return {?}
 */
(func) => {
    testingEnabled = true;
    hubCreationFunc = func;
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const unstarted = "unstarted";
/** @type {?} */
const connected = "connected";
/** @type {?} */
const disconnected = "disconnected";
/**
 * List of given states a SignalR can be.
 * @type {?}
 */
const SignalRStates = {
    unstarted,
    connected,
    disconnected,
};
/**
 * Convert a hub connection state to the internal state value.
 * \@param state The state of the hub connection.
 * @type {?}
 */
const toSignalRState = (/**
 * @param {?} state
 * @return {?}
 */
(state) => {
    switch (state) {
        case HubConnectionState.Connected:
            return connected;
        case HubConnectionState.Disconnected:
            return disconnected;
    }
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const getOrCreateSubject = (/**
 * @template T
 * @param {?} subjects
 * @param {?} event
 * @return {?}
 */
(subjects, event) => {
    return subjects[event] || (subjects[event] = new Subject());
});
/** @type {?} */
const createConnection = (/**
 * @param {?} url
 * @param {?=} options
 * @return {?}
 */
(url, options) => {
    if (!options) {
        return new HubConnectionBuilder().withUrl(url).build();
    }
    return new HubConnectionBuilder().withUrl(url, options).build();
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class SignalRHub {
    /**
     * @param {?} hubName
     * @param {?} url
     * @param {?} options
     */
    constructor(hubName, url, options) {
        this.hubName = hubName;
        this.url = url;
        this.options = options;
        this._startSubject = new Subject();
        this._stopSubject = new Subject();
        this._stateSubject = new Subject();
        this._errorSubject = new Subject();
        this._subjects = {};
        this.start$ = this._startSubject.asObservable();
        this.stop$ = this._stopSubject.asObservable();
        this.state$ = this._stateSubject.asObservable();
        this.error$ = this._errorSubject.asObservable();
    }
    /**
     * @private
     * @return {?}
     */
    ensureConnectionOpened() {
        if (!this._connection) {
            this._connection = createConnection(this.url, this.options);
            this._connection.onclose((/**
             * @param {?} error
             * @return {?}
             */
            (error) => {
                this._errorSubject.next(error);
                this._stateSubject.next(disconnected);
            }));
        }
        return this._connection;
    }
    /**
     * @return {?}
     */
    start() {
        /** @type {?} */
        const connection = this.ensureConnectionOpened();
        connection
            .start()
            .then((/**
         * @param {?} _
         * @return {?}
         */
        (_) => {
            this._startSubject.next();
            this._stateSubject.next(connected);
        }))
            .catch((/**
         * @param {?} error
         * @return {?}
         */
        (error) => this._startSubject.error(error)));
        return this._startSubject.asObservable();
    }
    /**
     * @return {?}
     */
    stop() {
        if (!this._connection) {
            return throwError("The connection has not been started yet. Please start the connection by invoking the start method before attempting to stop listening from the server.");
        }
        this._connection
            .stop()
            .then((/**
         * @param {?} _
         * @return {?}
         */
        (_) => {
            this._stopSubject.next();
            this._stateSubject.next(disconnected);
        }))
            .catch((/**
         * @param {?} error
         * @return {?}
         */
        (error) => this._stopSubject.error(error)));
        return this._stopSubject.asObservable();
    }
    /**
     * @template T
     * @param {?} eventName
     * @return {?}
     */
    on(eventName) {
        /** @type {?} */
        const connection = this.ensureConnectionOpened();
        /** @type {?} */
        const subject = getOrCreateSubject(this._subjects, eventName);
        connection.on(eventName, (/**
         * @param {?} data
         * @return {?}
         */
        (data) => subject.next(data)));
        return subject.asObservable();
    }
    /**
     * @param {?} eventName
     * @return {?}
     */
    off(eventName) {
        if (!this._connection) {
            return throwError("The connection has not been started yet. Please start the connection by invoking the start method before attempting to stop listening from the server.");
        }
        this._connection.off(eventName);
    }
    /**
     * @template T
     * @param {?} methodName
     * @param {...?} args
     * @return {?}
     */
    stream(methodName, ...args) {
        return new Observable((/**
         * @param {?} observer
         * @return {?}
         */
        (observer) => {
            /** @type {?} */
            const connection = this.ensureConnectionOpened();
            /** @type {?} */
            const stream = connection.stream(methodName, ...args);
            /** @type {?} */
            const subscription = stream.subscribe(observer);
            return (/**
             * @return {?}
             */
            () => subscription.dispose());
        })).pipe(share());
    }
    /**
     * @template T
     * @param {?} methodName
     * @param {...?} args
     * @return {?}
     */
    send(methodName, ...args) {
        if (!this._connection) {
            return throwError("The connection has not been started yet. Please start the connection by invoking the start method before attempting to send a message to the server.");
        }
        return from(this._connection.invoke(methodName, ...args));
    }
    /**
     * @template T
     * @param {?} methodName
     * @param {?} observable
     * @return {?}
     */
    sendStream(methodName, observable) {
        /** @type {?} */
        const connection = this.ensureConnectionOpened();
        /** @type {?} */
        const internalSubject = new Subject$1();
        observable.subscribe(internalSubject);
        connection.send(methodName, internalSubject);
    }
    /**
     * @return {?}
     */
    hasSubscriptions() {
        for (let key in this._subjects) {
            if (this._subjects.hasOwnProperty(key)) {
                return true;
            }
        }
        return false;
    }
}
if (false) {
    /**
     * @type {?}
     * @private
     */
    SignalRHub.prototype._connection;
    /**
     * @type {?}
     * @private
     */
    SignalRHub.prototype._startSubject;
    /**
     * @type {?}
     * @private
     */
    SignalRHub.prototype._stopSubject;
    /**
     * @type {?}
     * @private
     */
    SignalRHub.prototype._stateSubject;
    /**
     * @type {?}
     * @private
     */
    SignalRHub.prototype._errorSubject;
    /**
     * @type {?}
     * @private
     */
    SignalRHub.prototype._subjects;
    /** @type {?} */
    SignalRHub.prototype.start$;
    /** @type {?} */
    SignalRHub.prototype.stop$;
    /** @type {?} */
    SignalRHub.prototype.state$;
    /** @type {?} */
    SignalRHub.prototype.error$;
    /** @type {?} */
    SignalRHub.prototype.hubName;
    /** @type {?} */
    SignalRHub.prototype.url;
    /** @type {?} */
    SignalRHub.prototype.options;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const hubs = [];
/**
 * @param {?} x
 * @param {?=} url
 * @return {?}
 */
function findHub(x, url) {
    if (typeof x === "string") {
        return hubs.filter((/**
         * @param {?} h
         * @return {?}
         */
        (h) => h.hubName === x && h.url === url))[0];
    }
    return hubs.filter((/**
     * @param {?} h
     * @return {?}
     */
    (h) => h.hubName === x.hubName && h.url === x.url))[0];
}
/**
 * Create a new SignalR hub instance.
 * \@param hubName Name of the hub.
 * \@param url Url of the hub.
 * \@param options Configuration of the hub.
 * @type {?}
 */
const createHub = (/**
 * @param {?} hubName
 * @param {?} url
 * @param {?=} options
 * @return {?}
 */
(hubName, url, options) => {
    if (testingEnabled) {
        /** @type {?} */
        const hub = hubCreationFunc(hubName, url, options);
        if (hub) {
            hubs.push(hub);
            return hub;
        }
        return undefined;
    }
    /** @type {?} */
    const hub = new SignalRHub(hubName, url, options);
    hubs.push(hub);
    return hub;
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Returns an Observable with the current status of network connection, whether online or offline.
 * @type {?}
 */
const isOnline$ = (/**
 * @return {?}
 */
() => {
    /** @type {?} */
    const offline$ = fromEvent(window, "offline").pipe(map((/**
     * @return {?}
     */
    () => false)));
    /** @type {?} */
    const online$ = fromEvent(window, "online").pipe(map((/**
     * @return {?}
     */
    () => true)));
    return merge(offline$, online$).pipe(startWith(navigator.onLine));
});
/**
 * @param {?} x
 * @param {?=} url
 * @return {?}
 */
function ofHub(x, url) {
    if (typeof x === "string") {
        return filter((/**
         * @param {?} action
         * @return {?}
         */
        (action) => action.hubName === x && action.url === url));
    }
    else {
        return filter((/**
         * @param {?} action
         * @return {?}
         */
        (action) => action.hubName === x.hubName && action.url === x.url));
    }
}
/**
 * Map the hub definition (hub name, url) to an existing SignalR hub instance.
 * @type {?}
 */
const mapToHub = (/**
 * @return {?}
 */
() => map(findHub));
/** @type {?} */
const hubAndActionOrNotFound = (/**
 * @template T, U
 * @param {?} func
 * @return {?}
 */
(func) => (/**
 * @param {?} action
 * @return {?}
 */
(action) => {
    /** @type {?} */
    const search = (/** @type {?} */ (((/** @type {?} */ (action)))));
    /** @type {?} */
    const hub = findHub(search);
    if (!hub) {
        return of(hubNotFound(search));
    }
    return func({ action, hub });
}));
const ɵ0 = hubAndActionOrNotFound;
/**
 * Map a hub action to a new object that contains
 * both the original action and the SignalR hub instance,
 * using `mergeMap` rxjs operator.
 * \@param func A function that returns an Observable according to the given action and SignalR hub instance.
 * @type {?}
 */
const mergeMapHubToAction = (/**
 * @template T, U
 * @param {?} func
 * @return {?}
 */
(func) => mergeMap(hubAndActionOrNotFound(func)));
/**
 * Map a hub action to a new object that contains
 * both the original action and the SignalR hub instance,
 * using `switchMap` rxjs operator.
 * \@param func A function that returns an Observable according to the given action and SignalR hub instance.
 * @type {?}
 */
const switchMapHubToAction = (/**
 * @template T, U
 * @param {?} func
 * @return {?}
 */
(func) => switchMap(hubAndActionOrNotFound(func)));
/**
 * Map a hub action to a new object that contains
 * both the original action and the SignalR hub instance,
 * using `exhaustMap` rxjs operator.
 * \@param func A function that returns an Observable according to the given action and SignalR hub instance.
 * @type {?}
 */
const exhaustMapHubToAction = (/**
 * @template T, U
 * @param {?} func
 * @return {?}
 */
(func) => exhaustMap(hubAndActionOrNotFound(func)));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Collection of effects to execute realtime events (hub creation, starting, stopping, etc..).
 */
class SignalREffects {
    /**
     * @param {?} actions$
     */
    constructor(actions$) {
        this.actions$ = actions$;
        /**
         * Automatically create a new SignalR hub (then set hub state to `unstarted` by default).
         */
        this.createHub$ = createEffect((/**
         * @return {?}
         */
        () => this.actions$.pipe(ofType(createSignalRHub), map((/**
         * @param {?} action
         * @return {?}
         */
        (action) => {
            /** @type {?} */
            const hub = createHub(action.hubName, action.url, action.options);
            if (!hub) {
                return signalrError({
                    hubName: action.hubName,
                    url: action.url,
                    error: "Unable to create SignalR hub...",
                });
            }
            return signalrHubUnstarted({ hubName: hub.hubName, url: hub.url });
        })))));
        /**
         * Listen to every change on the SignalR hub.
         * Listen to start result (success/fail).
         * Listen to change connection state (connected, disconnected).
         * Listen to hub error.
         */
        this.beforeStartHub$ = createEffect((/**
         * @return {?}
         */
        () => this.actions$.pipe(ofType(signalrHubUnstarted), mergeMapHubToAction((/**
         * @param {?} __0
         * @return {?}
         */
        ({ hub, action }) => {
            /** @type {?} */
            const start$ = hub.start$.pipe(mergeMap((/**
             * @param {?} _
             * @return {?}
             */
            (_) => EMPTY)), catchError((/**
             * @param {?} error
             * @return {?}
             */
            (error) => of(signalrHubFailedToStart({
                hubName: action.hubName,
                url: action.url,
                error,
            })))));
            /** @type {?} */
            const state$ = hub.state$.pipe(mergeMap((/**
             * @param {?} state
             * @return {?}
             */
            (state) => {
                if (state === connected) {
                    return of(signalrConnected({ hubName: action.hubName, url: action.url }));
                }
                if (state === disconnected) {
                    return of(signalrDisconnected({
                        hubName: action.hubName,
                        url: action.url,
                    }));
                }
                return EMPTY;
            })));
            /** @type {?} */
            const error$ = hub.error$.pipe(map((/**
             * @param {?} error
             * @return {?}
             */
            (error) => signalrError({ hubName: action.hubName, url: action.url, error }))));
            return merge(start$, state$, error$);
        })))));
        /**
         * Automatically start hub based on actions dispatched.
         */
        this.startHub$ = createEffect((/**
         * @return {?}
         */
        () => this.actions$.pipe(ofType(startSignalRHub, reconnectSignalRHub), mergeMapHubToAction((/**
         * @param {?} __0
         * @return {?}
         */
        ({ hub }) => {
            return hub.start().pipe(mergeMap((/**
             * @param {?} _
             * @return {?}
             */
            (_) => EMPTY)), catchError((/**
             * @param {?} error
             * @return {?}
             */
            (error) => of(signalrError({ hubName: hub.hubName, url: hub.url, error })))));
        })))));
        /**
         * Automatically stop hub based on actions dispatched.
         */
        this.stopHub$ = createEffect((/**
         * @return {?}
         */
        () => this.actions$.pipe(ofType(stopSignalRHub), mergeMapHubToAction((/**
         * @param {?} __0
         * @return {?}
         */
        ({ hub }) => {
            return hub.stop().pipe(mergeMap((/**
             * @param {?} _
             * @return {?}
             */
            (_) => EMPTY)), catchError((/**
             * @param {?} error
             * @return {?}
             */
            (error) => of(signalrError({ hubName: hub.hubName, url: hub.url, error })))));
        })))));
    }
}
SignalREffects.ɵfac = function SignalREffects_Factory(t) { return new (t || SignalREffects)(ɵngcc0.ɵɵinject(ɵngcc1.Actions)); };
SignalREffects.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: SignalREffects, factory: SignalREffects.ɵfac, providedIn: "root" });
/** @nocollapse */
SignalREffects.ctorParameters = () => [
    { type: Actions }
];
/** @nocollapse */ SignalREffects.ngInjectableDef = ɵɵdefineInjectable({ factory: function SignalREffects_Factory() { return new SignalREffects(ɵɵinject(Actions)); }, token: SignalREffects, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(SignalREffects, [{
        type: Injectable,
        args: [{
                providedIn: "root"
            }]
    }], function () { return [{ type: ɵngcc1.Actions }]; }, null); })();
if (false) {
    /**
     * Automatically create a new SignalR hub (then set hub state to `unstarted` by default).
     * @type {?}
     */
    SignalREffects.prototype.createHub$;
    /**
     * Listen to every change on the SignalR hub.
     * Listen to start result (success/fail).
     * Listen to change connection state (connected, disconnected).
     * Listen to hub error.
     * @type {?}
     */
    SignalREffects.prototype.beforeStartHub$;
    /**
     * Automatically start hub based on actions dispatched.
     * @type {?}
     */
    SignalREffects.prototype.startHub$;
    /**
     * Automatically stop hub based on actions dispatched.
     * @type {?}
     */
    SignalREffects.prototype.stopHub$;
    /**
     * @type {?}
     * @private
     */
    SignalREffects.prototype.actions$;
}
/** @type {?} */
const TEN_SECONDS = 10 * 1000;
/**
 * Create an \@ngrx effect to handle SignalR reconnection automatically.
 * \@param actions$ Observable of all actions dispatched in the current app.
 * \@param options Options to configure the effect.
 * @type {?}
 */
const createReconnectEffect = (/**
 * @param {?} actions$
 * @param {?=} options
 * @return {?}
 */
(actions$, options) => {
    /** @type {?} */
    const defaultReconnect$ = timer(0, TEN_SECONDS).pipe(switchMap(isOnline$), filter((/**
     * @param {?} isOnline
     * @return {?}
     */
    (isOnline) => isOnline)));
    return createEffect((/**
     * @return {?}
     */
    () => actions$.pipe(ofType(signalrDisconnected), filter((/**
     * @param {?} action
     * @return {?}
     */
    (action) => !options || !options.hubName || action.hubName === options.hubName)), groupBy((/**
     * @param {?} action
     * @return {?}
     */
    (action) => action.hubName)), mergeMap((/**
     * @param {?} group
     * @return {?}
     */
    (group) => group.pipe(exhaustMapHubToAction((/**
     * @param {?} __0
     * @return {?}
     */
    ({ action }) => {
        /** @type {?} */
        const reconnect$ = options && options.reconnectionPolicy
            ? options.reconnectionPolicy(action)
            : defaultReconnect$;
        return reconnect$.pipe(map((/**
         * @param {?} _
         * @return {?}
         */
        (_) => reconnectSignalRHub(action))), takeUntil(actions$.pipe(ofType(signalrConnected), ofHub(action))));
    })), takeUntil(actions$.pipe(ofType(stopSignalRHub), filter((/**
     * @param {?} action
     * @return {?}
     */
    (action) => action.hubName === group.key))))))))));
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * @abstract
 */
class SignalRTestingHub {
    /**
     * @param {?} hubName
     * @param {?} url
     * @param {?} options
     */
    constructor(hubName, url, options) {
        this.hubName = hubName;
        this.url = url;
        this.options = options;
        this._startSubject = new Subject();
        this._stopSubject = new Subject();
        this._stateSubject = new Subject();
        this._errorSubject = new Subject();
        this._subjects = {};
        this.start$ = this._startSubject.asObservable();
        this.stop$ = this._stopSubject.asObservable();
        this.state$ = this._stateSubject.asObservable();
        this.error$ = this._errorSubject.asObservable();
    }
    /**
     * @return {?}
     */
    start() {
        timer(100).subscribe((/**
         * @param {?} _
         * @return {?}
         */
        (_) => {
            this._startSubject.next();
            this._stateSubject.next(connected);
        }));
        return this._startSubject.asObservable();
    }
    /**
     * @return {?}
     */
    stop() {
        timer(100).subscribe((/**
         * @param {?} _
         * @return {?}
         */
        (_) => {
            this._stopSubject.next();
            this._stateSubject.next(disconnected);
        }));
        return this._stopSubject.asObservable();
    }
    /**
     * @return {?}
     */
    hasSubscriptions() {
        for (let key in this._subjects) {
            if (this._subjects.hasOwnProperty(key)) {
                return true;
            }
        }
        return false;
    }
}
if (false) {
    /**
     * @type {?}
     * @private
     */
    SignalRTestingHub.prototype._startSubject;
    /**
     * @type {?}
     * @private
     */
    SignalRTestingHub.prototype._stopSubject;
    /**
     * @type {?}
     * @private
     */
    SignalRTestingHub.prototype._stateSubject;
    /**
     * @type {?}
     * @private
     */
    SignalRTestingHub.prototype._errorSubject;
    /**
     * @type {?}
     * @private
     */
    SignalRTestingHub.prototype._subjects;
    /** @type {?} */
    SignalRTestingHub.prototype.start$;
    /** @type {?} */
    SignalRTestingHub.prototype.stop$;
    /** @type {?} */
    SignalRTestingHub.prototype.state$;
    /** @type {?} */
    SignalRTestingHub.prototype.error$;
    /** @type {?} */
    SignalRTestingHub.prototype.hubName;
    /** @type {?} */
    SignalRTestingHub.prototype.url;
    /** @type {?} */
    SignalRTestingHub.prototype.options;
    /**
     * @abstract
     * @template T
     * @param {?} eventName
     * @return {?}
     */
    SignalRTestingHub.prototype.on = function (eventName) { };
    /**
     * @abstract
     * @param {?} eventName
     * @return {?}
     */
    SignalRTestingHub.prototype.off = function (eventName) { };
    /**
     * @abstract
     * @template T
     * @param {?} methodName
     * @param {...?} args
     * @return {?}
     */
    SignalRTestingHub.prototype.stream = function (methodName, args) { };
    /**
     * @abstract
     * @template T
     * @param {?} methodName
     * @param {...?} args
     * @return {?}
     */
    SignalRTestingHub.prototype.send = function (methodName, args) { };
    /**
     * @abstract
     * @template T
     * @param {?} methodName
     * @param {?} observable
     * @return {?}
     */
    SignalRTestingHub.prototype.sendStream = function (methodName, observable) { };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const initialState = {
    hubStatuses: [],
};
/**
 * Base class that store the entire state of SignalR in the app Store.
 * @record
 */
function BaseSignalRStoreState() { }
if (false) {
    /**
     * Status of all hubs created in the app.
     * @type {?}
     */
    BaseSignalRStoreState.prototype.hubStatuses;
}
const ɵ0$1 = /**
 * @param {?} state
 * @param {?} action
 * @return {?}
 */
(state, action) => (Object.assign({}, state, { hubStatuses: state.hubStatuses.concat([
        {
            hubName: action.hubName,
            url: action.url,
            state: (/** @type {?} */ (unstarted)),
        },
    ]) })), ɵ1 = /**
 * @param {?} state
 * @param {?} action
 * @return {?}
 */
(state, action) => {
    return Object.assign({}, state, { hubStatuses: state.hubStatuses.map((/**
         * @param {?} hs
         * @return {?}
         */
        (hs) => {
            if (hs.hubName === action.hubName && hs.url === action.url) {
                return Object.assign({}, hs, { state: (/** @type {?} */ (unstarted)) });
            }
            return hs;
        })) });
}, ɵ2 = /**
 * @param {?} state
 * @param {?} action
 * @return {?}
 */
(state, action) => {
    return Object.assign({}, state, { hubStatuses: state.hubStatuses.map((/**
         * @param {?} hs
         * @return {?}
         */
        (hs) => {
            if (hs.hubName === action.hubName && hs.url === action.url) {
                return Object.assign({}, hs, { state: (/** @type {?} */ (connected)) });
            }
            return hs;
        })) });
}, ɵ3 = /**
 * @param {?} state
 * @param {?} action
 * @return {?}
 */
(state, action) => {
    return Object.assign({}, state, { hubStatuses: state.hubStatuses.map((/**
         * @param {?} hs
         * @return {?}
         */
        (hs) => {
            if (hs.hubName === action.hubName && hs.url === action.url) {
                return Object.assign({}, hs, { state: (/** @type {?} */ (disconnected)) });
            }
            return hs;
        })) });
};
/** @type {?} */
const reducer = createReducer(initialState, on(createSignalRHub, (ɵ0$1)), on(signalrHubUnstarted, (ɵ1)), on(signalrConnected, (ɵ2)), on(signalrDisconnected, (ɵ3)));
/**
 * A reducer to use on the SignalR store state.
 * @param {?} state A state that contains SignalR hub information.
 * @param {?} action An action to dispatch.
 * @return {?}
 */
function signalrReducer(state, action) {
    return reducer(state, action);
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Default SignalR feature name, mainly used in the root reducer.
 * @type {?}
 */
const DEFAULT_SIGNALR_FEATURENAME = "signalr";

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * @record
 */
function RootState() { }
if (false) {
    /** @type {?} */
    RootState.prototype.signalr;
}
/**
 * Feature selector to the select the part of the state about SignalR.
 * \@param state Root state.
 * @type {?}
 */
const selectSignalrState = (/**
 * @param {?} state
 * @return {?}
 */
(state) => state[DEFAULT_SIGNALR_FEATURENAME]);
const ɵ0$2 = /**
 * @param {?} state
 * @return {?}
 */
(state) => state.hubStatuses;
/**
 * Select all hub statuses.
 * @type {?}
 */
const selectHubsStatuses = createSelector(selectSignalrState, (ɵ0$2));
const ɵ1$1 = /**
 * @param {?} state
 * @param {?} __1
 * @return {?}
 */
(state, { hubName, url }) => state.hubStatuses.filter((/**
 * @param {?} hs
 * @return {?}
 */
(hs) => hs.hubName === hubName && hs.url === url))[0];
/**
 * Select a single hub status.
 * @type {?}
 */
const selectHubStatus = createSelector(selectSignalrState, (ɵ1$1));
const ɵ2$1 = /**
 * @param {?} hubStatuses
 * @return {?}
 */
(hubStatuses) => hubStatuses.every((/**
 * @param {?} hs
 * @return {?}
 */
(hs) => hs.state === connected));
/**
 * Select a value (true or false) when all hubs are connected.
 * @type {?}
 */
const selectAreAllHubsConnected = createSelector(selectHubsStatuses, (ɵ2$1));
const ɵ3$1 = /**
 * @param {?} state
 * @param {?} props
 * @return {?}
 */
(state, props) => !!state && state.state === props.state;
/**
 * Select a value (true or false) when a single hub have a given status (unstarted, connected, disconnected).
 * @type {?}
 */
const selectHasHubState = createSelector(selectHubStatus, (ɵ3$1));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class StoreSignalRService {
    /**
     * @param {?} x
     * @param {?=} url
     * @return {?}
     */
    findHub(x, url) {
        if (typeof x === "string") {
            return findHub(x, url || "");
        }
        else {
            return findHub(x.hubName, x.url);
        }
    }
    /**
     * @param {?} hubName
     * @param {?} url
     * @param {?=} options
     * @return {?}
     */
    createHub(hubName, url, options) {
        return createHub(hubName, url, options);
    }
}
StoreSignalRService.ɵfac = function StoreSignalRService_Factory(t) { return new (t || StoreSignalRService)(); };
StoreSignalRService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: StoreSignalRService, factory: StoreSignalRService.ɵfac, providedIn: "root" });
/** @nocollapse */ StoreSignalRService.ngInjectableDef = ɵɵdefineInjectable({ factory: function StoreSignalRService_Factory() { return new StoreSignalRService(); }, token: StoreSignalRService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(StoreSignalRService, [{
        type: Injectable,
        args: [{
                providedIn: "root"
            }]
    }], null, null); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

export { SIGNALR_CONNECTED, SIGNALR_DISCONNECTED, SIGNALR_ERROR, SIGNALR_HUB_FAILED_TO_START, SIGNALR_HUB_UNSTARTED, SignalREffects, SignalRHub, SignalRStates, SignalRTestingHub, StoreSignalRService, createHub, createReconnectEffect, createSignalRHub, enableTesting, exhaustMapHubToAction, findHub, hubNotFound, mapToHub, mergeMapHubToAction, ofHub, reconnectSignalRHub, selectAreAllHubsConnected, selectHasHubState, selectHubStatus, selectHubsStatuses, selectSignalrState, signalrConnected, signalrDisconnected, signalrError, signalrHubFailedToStart, signalrHubUnstarted, signalrReducer, startSignalRHub, stopSignalRHub, switchMapHubToAction, testingEnabled, unstarted as ɵa, connected as ɵb, disconnected as ɵc };

